<template>
    <DForm @submit.prevent="submitForm">
        <DFormRow
            :label="__('products.productGroups.form.name')"
            is-required
        >
            <DInput
                id="name"
                v-model="form.name"
                :placeholder="__('products.productGroups.form.name')"
                add-input-classes="bg-light"
                :is-valid="isFormFieldValid('name')"
                :invalid-feedback="errors.name"
                maxlength="191"
            />
        </DFormRow>
        <DFormRow
            :label="__('products.productGroups.form.brands')"
            is-required
        >
            <DMultiSelect
                id="brands"
                v-model:options="brandOptions"
                :search="true"
                :selected="form.product_brands"
                :selection="true"
                :options-empty-placeholder="__('products.productGroups.form.no_options_found')"
                :search-placeholder="__('products.productGroups.form.assign_brands')"
                selection-type="text"
                :is-valid="isFormFieldValid('product_brands')"
                :invalid-feedback="errors.product_brands"
                @update="updateSelectedBrands"
            />
            <p
                v-if="errors['product_brands']"
                class="invalid-feedback d-block"
            >
                <span>
                    {{ errors['product_brands'] }}<br>
                </span>
            </p>
        </DFormRow>
        <DFormRow :label="__('products.productGroups.form.retired')">
            <div
                role="group"
                class="form-group"
            >
                <DSwitch
                    id="switch-retired"
                    v-model="form.retired"
                    color="success"
                    variant="3d"
                />
            </div>
        </DFormRow>
        <DFormRow :label="__('products.productGroups.form.possibleChildGroups')">
            <div class="group-selection">
                <DMultiSelect
                    id="child_product_groups"
                    :key="childGroupMultiSelectKey"
                    :options="availableChildProductGroups"
                    :search="true"
                    :selected="form.selected_child_groups"
                    :selection="true"
                    :options-empty-placeholder="__('products.productGroups.form.no_options_found')"
                    :search-placeholder="__('products.productGroups.form.addChildGroups')"
                    selection-type="tags"
                    @update="updateSelectedChildGroups"
                />
                <div class="retired-toggle">
                    <DSwitch
                        id="display_retired_child_product_groups"
                        v-model="displayRetiredChildProductGroups"
                        color="success"
                        variant="3d"
                        @update:checked="handleRetiredChildProductGroupsChange"
                    />
                    <label
                        for="display_retired_child_product_groups"
                        class="retired-toggle-label"
                    >
                        {{ __('products.productGroups.form.display_retired_groups') }}
                    </label>
                </div>
            </div>
            <p
                v-if="errors['selected_child_groups.0']"
                class="invalid-feedback d-block"
            >
                <span
                    v-for="(error, index) in errors"
                    :key="index"
                >
                    <span v-if="index.match('selected_child_groups')">
                        {{ error }}<br>
                    </span>
                </span>
            </p>
            <p
                v-if="errors['selected_child_groups']"
                class="invalid-feedback d-block"
            >
                {{ errors['selected_child_groups'] }}
            </p>
        </DFormRow>
        <DFormRow :label="__('products.productGroups.form.possibleParentGroups')">
            <div class="group-selection">
                <DMultiSelect
                    id="parent_product_groups"
                    :key="parentGroupMultiSelectKey"
                    :options="availableParentProductGroups"
                    :search="true"
                    :selected="form.selected_parent_groups"
                    :selection="true"
                    :options-empty-placeholder="__('products.productGroups.form.no_options_found')"
                    :search-placeholder="__('products.productGroups.form.addParentGroups')"
                    selection-type="tags"
                    @update="updateSelectedParentGroups"
                />
                <div class="retired-toggle">
                    <DSwitch
                        id="display_retired_parent_product_groups"
                        v-model="displayRetiredParentProductGroups"
                        color="success"
                        variant="3d"
                        @update:checked="handleRetiredParentProductGroupsChange"
                    />
                    <label
                        for="display_retired_parent_product_groups"
                        class="retired-toggle-label"
                    >
                        {{ __('products.productGroups.form.display_retired_groups') }}
                    </label>
                </div>
            </div>
            <p
                v-if="errors['selected_parent_groups.0']"
                class="invalid-feedback d-block"
            >
                <span
                    v-for="(error, index) in errors"
                    :key="index"
                >
                    <span v-if="index.match('selected_parent_groups')">
                        {{ error }}<br>
                    </span>
                </span>
            </p>
            <p
                v-if="errors['selected_parent_groups']"
                class="invalid-feedback d-block"
            >
                {{ errors['selected_parent_groups'] }}
            </p>
        </DFormRow>
        <DFormControls
            :cancel-route="cancelRoute"
            @submit="submitForm"
        />
    </DForm>
</template>

<script>
export default {
    name: 'ProductGroupForm',
    props: {
        errors: {
            type: Object,
            default: () => {
                return {}
            }
        },
        productGroup: {
            type: Object,
            default: () => {
                return {}
            }
        },
        allProductGroups: {
            type: Array,
            default () {
                return []
            }
        },
        selectedChildGroups: {
            type: Array,
            default () {
                return []
            }
        },
        selectedParentGroups: {
            type: Array,
            default () {
                return []
            }
        },
        brands: {
            type: Array,
            default () {
                return []
            }
        },
        selectedProductGroupBrands: {
            type: Array,
            default () {
                return []
            }
        }
    },
    emits: ['form-submitted'],
    data () {
        return {
            form: this.$inertia.form({
                name: this.productGroup?.name ?? null,
                retired: this.productGroup?.retired ?? false,
                selected_child_groups: this.selectedChildGroups ?? [],
                selected_parent_groups: this.selectedParentGroups ?? [],
                product_brands: this.selectedProductGroupBrands ?? []
            }),
            childGroupMultiSelectKey: 1,
            displayRetiredChildProductGroups: false,
            parentGroupMultiSelectKey: 1,
            displayRetiredParentProductGroups: false
        }
    },
    computed: {
        cancelRoute () {
            return this.$route('products.products.index')
        },
        availableChildProductGroups () {
            return (this.displayRetiredChildProductGroups
                ? this.allProductGroups
                : this.allProductGroups.filter((group) => {
                    return group.retired === false
                })).map((option) => ({ ...option, selected: this.selectedChildGroups.includes(option.value) }))
        },
        availableParentProductGroups () {
            return (this.displayRetiredParentProductGroups
                ? this.allProductGroups
                : this.allProductGroups.filter((group) => {
                    return group.retired === false
                })).map((option) => ({ ...option, selected: this.selectedParentGroups.includes(option.value) }))
        },
        brandOptions () {
            return this.brands.map((item) => {
                return { value: item.value, text: item.label, selected: this.selectedProductGroupBrands.includes(item.value) }
            })
        }
    },
    methods: {
        submitForm () {
            this.$emit('form-submitted', this.form)
        },
        updateSelectedChildGroups (values) {
            this.form.selected_child_groups = values
        },
        updateSelectedParentGroups (values) {
            this.form.selected_parent_groups = values
        },
        handleRetiredChildProductGroupsChange () {
            // Force re-render by changing the key
            this.childGroupMultiSelectKey++
        },
        handleRetiredParentProductGroupsChange () {
            // Force re-render by changing the key
            this.parentGroupMultiSelectKey++
        },
        updateSelectedBrands (values) {
            this.form.product_brands = values
        }
    }
}
</script>

<style scoped>
    .group-selection {
        display: flex;
        align-items: center;
        width: 100%;
    }
    .retired-toggle-label {
        margin-bottom: 0;
        padding-left: 5px;
    }
    .retired-toggle {
        display: flex;
        align-items: center;
        flex-shrink: 0;
        margin-left: 10px
    }
</style>
